import { useState, useEffect } from 'react';
import { declarationsApi } from '../services/api/endpoints/declarations';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { SyncService } from '../services/sync/SyncService';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import type { Declaration } from '../types/declarations';
import type { FiscalYear } from '../types/fiscal-year';

interface UseDeclarationsOptions {
  fiscalYear?: FiscalYear | null;
}

export function useDeclarations(options: UseDeclarationsOptions = {}) {
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { fiscalYear } = options;
  
  // Utiliser les stores pour les filtres globaux
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();
  
  // Utiliser l'exercice des options ou celui du store
  const activeFiscalYear = fiscalYear || currentFiscalYear;

  useEffect(() => {
    // ⚡ Vider les données de l'ancien exercice avant rechargement
    setDeclarations([]);
    loadDeclarations();
  }, [activeFiscalYear?.id, selectedBusinessUnit?.id]); // Reload when fiscal year OR business unit changes

  const loadDeclarations = async () => {
    try {
      setError(null);
      
      // Load from IndexedDB first
      let localDeclarations = await IndexedDBService.getAll<Declaration>('declarations');
      
      // Protection contre les données corrompues
      localDeclarations = Array.isArray(localDeclarations) ? localDeclarations : [];
      
      // Filtrer par unité commerciale côté client si définie
      if (effectiveFilter.businessUnitId) {
        localDeclarations = localDeclarations.filter(d => 
          (d as any).businessUnitId === effectiveFilter.businessUnitId ||
          effectiveFilter.businessUnitIds?.includes((d as any).businessUnitId) ||
          !(d as any).businessUnitId // Déclarations sans unité sont visibles partout
        );
      }
      
      if (localDeclarations.length > 0) {
        setDeclarations(localDeclarations);
        setLoading(false);
      }

      // If online, fetch from API and update local storage
      if (navigator.onLine) {
        // Prepare filters for API call (fiscalYearId n'est PAS un query param accepté par le backend)
        const filters: any = {};
        if (effectiveFilter.businessUnitId) {
          filters.businessUnitId = effectiveFilter.businessUnitId;
        }
        if (effectiveFilter.businessUnitType) {
          filters.businessUnitType = effectiveFilter.businessUnitType;
        }
        
        const response = await declarationsApi.getAll(filters);
        if (response.success && response.data) {
          // Protection contre les données non-array
          const apiData = Array.isArray(response.data.data) ? response.data.data : [];
          setDeclarations(apiData);
          // Update IndexedDB
          for (const declaration of apiData) {
            await IndexedDBService.update('declarations', declaration);
          }
        }
      }
    } catch (err) {
      console.error('Error loading declarations:', err);
      setError('Impossible de charger les déclarations fiscales');
    } finally {
      setLoading(false);
    }
  };

  const addDeclaration = async (data: Partial<Declaration>): Promise<Declaration> => {
    // Payload conforme à CreateDeclarationDto (backend)
    const createPayload = {
      type: data.type!,
      period: data.period!,
      amount: data.amount || 0,
      additionalFees: data.additionalFees || 0,
      penalties: data.penalties || 0,
      notes: data.notes,
    };

    // Construire un objet local complet pour l'affichage immédiat
    const localDeclaration: Declaration = {
      id: crypto.randomUUID(),
      ...createPayload,
      periodicity: data.periodicity!,
      dueDate: data.dueDate!,
      status: 'draft',
      justificationDocument: data.justificationDocument,
      declarationForm: data.declarationForm,
      reference: `${data.type}-${data.period}`,
      attachments: []
    };

    // Affichage optimiste
    await IndexedDBService.add('declarations', localDeclaration);
    setDeclarations(prev => [...prev, localDeclaration]);

    if (!navigator.onLine) {
      await SyncService.addToSyncQueue({
        id: localDeclaration.id,
        type: 'CREATE',
        entity: 'declarations',
        data: createPayload
      });
    } else {
      try {
        const response = await declarationsApi.create(createPayload as any);
        if (response.success && response.data) {
          const serverDeclaration = response.data;
          // Remplacer l'entrée locale par celle du serveur (ID correct)
          await IndexedDBService.delete('declarations', localDeclaration.id);
          const merged: Declaration = {
            ...localDeclaration,
            ...serverDeclaration,
            justificationDocument: data.justificationDocument,
            declarationForm: data.declarationForm,
          };
          await IndexedDBService.add('declarations', merged);
          setDeclarations(prev =>
            prev.map(d => d.id === localDeclaration.id ? merged : d)
          );

          // Persister les documents via les endpoints dédiés
          if (data.justificationDocument) {
            await declarationsApi.patch_justification(serverDeclaration.id, data.justificationDocument);
          }
          if (data.declarationForm) {
            await declarationsApi.patch_declarationForm(serverDeclaration.id, data.declarationForm);
          }

          return merged;
        }
      } catch (error) {
        console.error('Error creating declaration:', error);
      }
    }

    return localDeclaration;
  };

  const updateDeclaration = async (id: string, data: Partial<Declaration>): Promise<void> => {
    const declaration = declarations.find(d => d.id === id);
    if (!declaration) return;

    const updated = { ...declaration, ...data };
    setDeclarations(prev => prev.map(d => d.id === id ? updated : d));
    await IndexedDBService.update('declarations', updated);

    if (navigator.onLine) {
      try {
        await declarationsApi.update(id, data);
      } catch (error) {
        console.error('Error updating declaration:', error);
        throw error;
      }
    } else {
      await SyncService.addToSyncQueue({
        id,
        type: 'UPDATE',
        entity: 'declarations',
        data,
      });
    }
  };

  const validateDeclaration = async (id: string): Promise<void> => {
    const declaration = declarations.find(d => d.id === id);
    if (!declaration) return;

    if (navigator.onLine) {
      try {
        const response = await declarationsApi.validate(id);
        if (response.success && response.data) {
          const updated = { ...declaration, status: 'validated' as const };
          setDeclarations(prev => prev.map(d => d.id === id ? updated : d));
          await IndexedDBService.update('declarations', updated);
        }
      } catch (error) {
        console.error('Error validating declaration:', error);
        throw error;
      }
    } else {
      const updated = { ...declaration, status: 'validated' as const };
      setDeclarations(prev => prev.map(d => d.id === id ? updated : d));
      await IndexedDBService.update('declarations', updated);
      await SyncService.addToSyncQueue({ id, type: 'UPDATE', entity: 'declarations', data: { status: 'validated' } });
    }
  };

  const rejectDeclaration = async (id: string, reason?: string): Promise<void> => {
    const declaration = declarations.find(d => d.id === id);
    if (!declaration) return;

    if (navigator.onLine) {
      try {
        const response = await declarationsApi.reject(id, reason);
        if (response.success && response.data) {
          const updated = { ...declaration, status: 'rejected' as const };
          setDeclarations(prev => prev.map(d => d.id === id ? updated : d));
          await IndexedDBService.update('declarations', updated);
        }
      } catch (error) {
        console.error('Error rejecting declaration:', error);
        throw error;
      }
    } else {
      const updated = { ...declaration, status: 'rejected' as const };
      setDeclarations(prev => prev.map(d => d.id === id ? updated : d));
      await IndexedDBService.update('declarations', updated);
      await SyncService.addToSyncQueue({ id, type: 'UPDATE', entity: 'declarations', data: { status: 'rejected' } });
    }
  };

  const submitDeclaration = async (id: string) => {
    const declaration = declarations.find(d => d.id === id);
    if (!declaration) return;

    try {
      if (navigator.onLine) {
        const response = await declarationsApi.submit(id);
        if (response.success && response.data) {
          setDeclarations(prev => prev.map(d => 
            d.id === id ? { ...d, status: 'submitted' } : d
          ));
          await IndexedDBService.update('declarations', {
            ...declaration,
            status: 'submitted'
          });
        }
      } else {
        await SyncService.addToSyncQueue({
          id: declaration.id,
          type: 'UPDATE',
          entity: 'declarations',
          data: { ...declaration, status: 'submitted' }
        });
      }
    } catch (error) {
      console.error('Error submitting declaration:', error);
      throw error;
    }
  };

  return {
    declarations,
    loading,
    error,
    addDeclaration,
    updateDeclaration,
    submitDeclaration,
    validateDeclaration,
    rejectDeclaration,
    loadDeclarations,
    // Exposer les infos de filtre actuels
    currentFilters: {
      fiscalYearId: activeFiscalYear?.id,
      businessUnitId: selectedBusinessUnit?.id,
      businessUnitType: selectedBusinessUnit?.type
    }
  };
}